import { Link } from 'react-router-dom';
import { Card } from '../components/Card.js';
import { LatestFeeds } from '../components/LatestFeeds.js';
import { Loading } from '../components/Loading.js';
import { Tag } from '../components/Tag.js';
import { useEventsQuery, useMyTasksQuery, useMyTeamsQuery } from '../hooks/queries.js';
import { formatDate, schoolFullLabel } from '../lib/format.js';
import { taskPhaseLabel, taskStatusLabel, teamStatusLabel } from '../lib/labels.js';
import { useSession } from '../store/session.js';

/**
 * 首页仪表盘（登录后 / ）：一屏看清三件事——我的队伍、最近待办、竞赛资讯。
 * 数据全部来自既有查询（同一 queryKey 与各功能页共享缓存），本页不引入任何新请求模式。
 */
export function HomePage() {
  const user = useSession((state) => state.user);
  const teamsQuery = useMyTeamsQuery();
  const tasksQuery = useMyTasksQuery();
  const eventsQuery = useEventsQuery();

  const teams = teamsQuery.data?.teams ?? [];
  const tasks = tasksQuery.data?.tasks ?? [];
  const events = eventsQuery.data?.events ?? [];
  const openTasks = tasks.filter((task) => task.status !== 'done');

  const pending = teamsQuery.isPending || tasksQuery.isPending || eventsQuery.isPending;
  if (pending) {
    return (
      <div className="stack">
        <div className="page-head">
          <h1 className="page-title">首页</h1>
        </div>
        <Loading label="正在汇总你的数据…" rows={3} />
      </div>
    );
  }

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-title">{user ? `${user.name}，欢迎回来` : '欢迎回来'}</h1>
          <p className="page-sub">{user ? schoolFullLabel(user.school) : ''}</p>
        </div>
        <div className="row">
          <Link to="/find" className="btn btn-primary">
            找队友
          </Link>
          <Link to="/messages" className="btn btn-ghost">
            消息
          </Link>
        </div>
      </div>

      <div className="stat-grid">
        <Link to="/team" className="stat-card" data-tone="brand">
          <span className="stat-label">我的队伍</span>
          <span className="stat-value">{teams.length}</span>
          <span className="stat-foot">
            {teams.filter((team) => team.status === 'confirmed').length} 支已成队
          </span>
        </Link>
        <Link to="/tasks" className="stat-card" data-tone="warn">
          <span className="stat-label">未完成待办</span>
          <span className="stat-value">{openTasks.length}</span>
          <span className="stat-foot">{tasks.length} 条待办共</span>
        </Link>
        <Link to="/events" className="stat-card" data-tone="ok">
          <span className="stat-label">进行中赛事</span>
          <span className="stat-value">{events.length}</span>
          <span className="stat-foot">含跨校共性赛事</span>
        </Link>
        <Link to="/find" className="stat-card">
          <span className="stat-label">找队友入口</span>
          <span className="stat-value">
            3<small>条路</small>
          </span>
          <span className="stat-foot">AI 推荐 / 挑人 / 大厅</span>
        </Link>
      </div>

      <div className="grid-2">
        <Card title="我的队伍" subtitle={teams.length === 0 ? '还没有队伍' : `${teams.length} 支队伍`}>
          {teams.length === 0 ? (
            <p className="muted small">
              去<Link to="/find"> 找队友 </Link>：AI 匹配推荐、自己挑人、逛大厅申请，一条入口全搞定。
            </p>
          ) : (
            <div className="stack-sm">
              {teams.map((team) => (
                <div className="row-tight" key={team.id}>
                  <strong className="small">{team.name}</strong>
                  <Tag tone={team.status === 'confirmed' ? 'ok' : 'muted'}>{teamStatusLabel(team.status)}</Tag>
                  {team.eventCode ? <span className="muted small">{team.eventCode}</span> : null}
                  <Link to="/team" className="muted small right">
                    管理 →
                  </Link>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card
          title="最近待办"
          subtitle={openTasks.length === 0 ? '没有未完成的待办' : `${openTasks.length} 条待办未完成`}
        >
          {openTasks.length === 0 ? (
            <p className="muted small">
              成队后系统会自动生成 4 条阶段待办；也可以到赛事详情页一键生成备赛规划。
            </p>
          ) : (
            <div className="stack-sm">
              {openTasks.slice(0, 5).map((task) => (
                <div className="row-tight" key={task.id}>
                  <Tag tone="primary">{taskPhaseLabel(task.phase)}</Tag>
                  <span className="small wrap-anywhere">{task.title}</span>
                  <Tag tone="muted">{taskStatusLabel(task.status)}</Tag>
                  {task.dueDate ? <span className="muted small nowrap">{formatDate(task.dueDate)}</span> : null}
                </div>
              ))}
              {openTasks.length > 5 ? (
                <Link to="/tasks" className="muted small">
                  还有 {openTasks.length - 5} 条 →
                </Link>
              ) : null}
            </div>
          )}
        </Card>
      </div>

      <Card title="最新竞赛资讯" subtitle="从各校官网与赛事官网自动同步，每 30 分钟更新一次">
        <LatestFeeds events={events} />
        <div className="row">
          <Link to="/events" className="muted small">
            全部赛事与日历 →
          </Link>
        </div>
      </Card>
    </div>
  );
}
